<template>
  <div class="calendar">
    <div class="clock text-center" @click="reset">
      <div v-if="hasError" class="alert alert-danger mt-3">
        Es besteht ein Fehler mit der Verbindung zum Server.
      </div>
      <p class="h2">
        {{
          String(currentDate.getDate()).padStart(2, "0") +
          "." +
          String(parseInt(currentDate.getMonth()) + 1).padStart(2, "0") +
          "." +
          currentDate.getFullYear()
        }}
      </p>
      <p class="h1 mt-0">
        <span>{{ String(currentDate.getHours()).padStart(2, "0") }}</span>
        <span :style="{ opacity: showClockDots ? '1' : '0' }">:</span>
        <span>{{ String(currentDate.getMinutes()).padStart(2, "0") }}</span>
      </p>
    </div>
    <div class="row justify-content-between align-items-center mt-gutter">
      <div class="col-auto">
        <button class="btn btn-green icon" @click="previousMonth()">
          caret_left
        </button>
      </div>
      <div class="month col-auto">
        {{
          months[selectedMonth.getMonth()] + " " + selectedMonth.getFullYear()
        }}
      </div>
      <div class="col-auto">
        <button class="btn btn-green icon" @click="nextMonth()">
          caret_right
        </button>
      </div>
    </div>
    <div class="box mt-gutter-half">
      <table class="w-100 text-center">
        <thead>
          <th>Mo</th>
          <th>Di</th>
          <th>Mi</th>
          <th>Do</th>
          <th>Fr</th>
          <th>Sa</th>
          <th>So</th>
        </thead>
        <tbody>
          <tr v-for="(week, indexWeek) in calendarMatrix" :key="indexWeek">
            <td v-for="(day, indexDay) in week" :key="indexDay">
              <button
                class="btn"
                :class="{
                  today: isSameDay(day.date, currentDate),
                  selected: isSameDay(day.date, selectedDate),
                  'has-event': day.hasEvents,
                }"
                :disabled="!day.isInSelectedMonth"
                @click="selectDate(day.date)"
              >
                {{ day.date.getDate() }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { bookingCache } from "../store/bookingCache";

export default {
  props: {
    resource: String,
  },
  data() {
    return {
      currentDate: new Date(),
      selectedDate: new Date(),
      selectedMonth: new Date(),
      months: [
        "Januar",
        "Februar",
        "März",
        "April",
        "Mai",
        "Juni",
        "Juli",
        "August",
        "September",
        "Oktober",
        "November",
        "Dezember",
      ],
      calendarMatrix: [],
      showClockDots: true,
      hasError: false,
      events: [],
      cacheUnsubscribe: null,
    };
  },

  watch: {
    events() {
      this.updateCalendarMatrix(this.selectedMonth);
    },
  },
  created() {
    this.listenForNewEvents();

    // Subscribe to booking cache updates
    this.cacheUnsubscribe = bookingCache.subscribe((date, events) => {
      this.events = bookingCache.getAll();
    });
  },
  mounted() {
    this.updateCalendarMatrix(this.selectedMonth);

    // Only fetch today's events on initial load
    this.getTodaysEvents();

    setInterval(() => {
      this.updateTime();
    }, 1000);
  },
  beforeDestroy() {
    // Clean up Vue event bus listener
    if (typeof window !== "undefined" && window.vueEventBus) {
      window.vueEventBus.$off(
        "socketNotification",
        this.handleSocketNotification
      );
    }
    // Unsubscribe from cache updates
    if (this.cacheUnsubscribe) {
      this.cacheUnsubscribe();
    }
  },
  methods: {
    updateTime() {
      this.currentDate = new Date();
      this.showClockDots = false;
      setTimeout(() => {
        this.showClockDots = true;
      }, 500);
    },
    getCalendarMatrix(date) {
      let calendarMatrix = [];
      let firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      let lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      /*
      Modify the result of getDay so that we treat Monday = 0 instead of
      Sunday = 0
      */
      const firstWeekday = (firstDay.getDay() + 6) % 7;
      const lastWeekday = (lastDay.getDay() + 6) % 7;
      /*
      If the month didn't start on a Monday, start from the last Monday of
      the previous month
      */
      firstDay.setDate(firstDay.getDate() - firstWeekday);
      /*
      If the month didn't end on a Sunday, end on the following Sunday in the
      next month
      */
      lastDay.setDate(lastDay.getDate() + (6 - lastWeekday));
      /*
      If the month has not enough weeks to fill the calendar, add more
      */
      /* eslint-disable-next-line */
      while ((lastDay - firstDay) / (1000 * 3600 * 24) <= 35) {
        lastDay.setDate(lastDay.getDate() + (13 - lastWeekday));
      }

      let week = [];

      /* eslint-disable-next-line */
      while (firstDay <= lastDay) {
        let hasEvents = false;

        if (this.events) {
          for (let i = 0; i < this.events.length - 1; i++) {
            let event = this.events[i];

            if (this.isSameDay(firstDay, event.startDate)) {
              hasEvents = true;
            }
          }
        }

        week.push({
          date: new Date(firstDay),
          isInSelectedMonth: firstDay.getMonth() === date.getMonth(),
          hasEvents: hasEvents,
        });

        if (week.length === 7) {
          calendarMatrix.push(week);
          week = [];
        }

        firstDay.setDate(firstDay.getDate() + 1);
      }

      return calendarMatrix;
    },
    selectDate(date) {
      this.selectedDate = date;

      // Fetch events for the selected date
      this.getEventsForDate(date);

      this.$emit("select-date", this.selectedDate);
    },
    updateCalendarMatrix(date) {
      this.calendarMatrix = this.getCalendarMatrix(date);
    },
    isSameDay(firstDate, secondDate) {
      if (
        firstDate.getFullYear() === secondDate.getFullYear() &&
        firstDate.getMonth() === secondDate.getMonth() &&
        firstDate.getDate() === secondDate.getDate()
      ) {
        return true;
      } else {
        return false;
      }
    },
    nextMonth() {
      this.selectedMonth.setMonth(this.selectedMonth.getMonth() + 1);
      this.onSelectMonth();
    },
    previousMonth() {
      this.selectedMonth.setMonth(this.selectedMonth.getMonth() - 1);
      this.onSelectMonth();
    },
    onSelectMonth() {
      this.updateCalendarMatrix(this.selectedMonth);
      this.$emit("select-month", this.selectedMonth);

      // Optional: Prefetch the entire month in background
      // Uncomment if you want to load all dates when month changes
      // this.prefetchMonth()
    },
    reset() {
      this.selectedMonth.setMonth(this.currentDate.getMonth());
      this.selectedMonth.setYear(this.currentDate.getFullYear());
      this.updateCalendarMatrix(this.selectedMonth);
      this.selectDate(this.currentDate);
      // selectDate() will fetch today's events
    },
    // Fetch only today's events on initial load
    async getTodaysEvents() {
      try {
        // eslint-disable-next-line no-console
        console.log("📅 Loading today's events only");
        await bookingCache.fetchDate(this.resource, this.currentDate);
        this.events = bookingCache.getAll();

        if (this.hasError) {
          this.hasError = false;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error loading today's events:", error);
        this.hasError = true;
      }
    },

    // Fetch events for a specific date when clicked
    async getEventsForDate(date) {
      try {
        // eslint-disable-next-line no-console
        console.log(
          "📅 Loading events for selected date:",
          date.toDateString()
        );
        await bookingCache.fetchDate(this.resource, date);
        this.events = bookingCache.getAll();

        if (this.hasError) {
          this.hasError = false;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error loading events for date:", error);
        this.hasError = true;
      }
    },

    // Optional: Prefetch entire month in background
    async prefetchMonth() {
      try {
        const dateFrom = new Date(
          this.selectedMonth.getFullYear(),
          this.selectedMonth.getMonth(),
          1
        );
        const dateTo = new Date(
          this.selectedMonth.getFullYear(),
          this.selectedMonth.getMonth() + 1,
          0
        );

        // eslint-disable-next-line no-console
        console.log("📅 Prefetching month in background...");
        await bookingCache.prefetchRange(this.resource, dateFrom, dateTo);
        this.events = bookingCache.getAll();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error prefetching month:", error);
      }
    },

    handleSocketNotification(data) {
      // eslint-disable-next-line no-undef
      if (parseInt(data.manager_id, 10) === parseInt(MANAGER_ID, 10)) {
        // eslint-disable-next-line no-console
        console.log("🔔 New event notification received, clearing cache");
        // Clear cache and reload current date
        bookingCache.clear();
        this.getEventsForDate(this.selectedDate);
      }
    },
    listenForNewEvents() {
      // Listen to Vue event bus (compatible with main app script.js)
      // This receives notifications from both main app and resource-widget socket
      if (typeof window !== "undefined" && window.vueEventBus) {
        window.vueEventBus.$on(
          "socketNotification",
          this.handleSocketNotification
        );
      }
    },
  },
};
</script>
